import { BadgeCheck, GraduationCap, ScanLine, ShieldCheck, Star, Quote } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow, CaptionReveal } from "../components/Reveal";
import Marquee from "../components/Marquee";
import LearnMore from "../components/LearnMore";
import { GoogleBadge, REAL_REVIEW } from "../components/Testimonials";
import useSEO from "../hooks/useSEO";

const IMG = {
  clinic: "https://images.pexels.com/photos/305567/pexels-photo-305567.jpeg?auto=compress&cs=tinysrgb&w=1600",
  room1: "https://images.pexels.com/photos/4269265/pexels-photo-4269265.jpeg?auto=compress&cs=tinysrgb&w=1400",
  room2: "https://images.pexels.com/photos/9062527/pexels-photo-9062527.jpeg?auto=compress&cs=tinysrgb&w=1200",
  smile: "https://images.unsplash.com/photo-1548661211-e559d8c17537?auto=format&fit=crop&q=85&w=1200",
  gattuso: "https://customer-assets-v7afamib.emergentagent.net/job_chivasso-restyle/artifacts/1292b2ec0660050e_3%20foto%20Gattuso.webp",
};

const TEAM_ROLES = [
  { icon: BadgeCheck, t: "Ortodonzia e pedodonzia", d: "Invisalign®, brackets e percorsi delicati per i più piccoli." },
  { icon: ShieldCheck, t: "Protesi ed estetica", d: "Protesi in zirconio, faccette e ricostruzioni del sorriso." },
  { icon: ScanLine, t: "Parodontologia e igiene", d: "Trattamento della parodontite e manutenzione periodica." },
];

const CERTS = [
  { icon: ShieldCheck, t: "Impianti 100% italiani", d: "Materiali certificati e tracciabili, dal produttore alla tua bocca." },
  { icon: ScanLine, t: "Diagnostica in sede", d: "Ortopantomografo digitale e TAC dental scan, senza appuntamenti esterni." },
  { icon: GraduationCap, t: "Docenza universitaria", d: "Il Dott. Gattuso è docente al Master dell'Università La Sapienza di Roma." },
];

export default function Studio() {
  useSEO({
    title: "Lo Studio | Dentalchivasso — Dr. Gattuso e il team dentistico a Chivasso",
    description: "Scopri lo Studio Dentistico Dentalchivasso in Via Gerbido 15/3, Chivasso: il Dr. Gattuso (25 anni di implantologia avanzata), il Dr. Giorgi e tutto il team, con TAC 3D e ortopantomografo in sede.",
    path: "/studio",
  });
  return (
    <main>
      {/* ---- HERO ---- */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-40 pb-20" data-testid="studio-hero">
        <Eyebrow>Lo Studio</Eyebrow>
        <MaskedLines
          className="mt-6 font-display font-medium tracking-tight text-ink text-5xl sm:text-6xl leading-[1.04]"
          delay={0.2}
          lines={[
            "Tecnologia all'avanguardia,",
            <span key="2" className="italic text-teal">calore di famiglia.</span>,
          ]}
        />
        <p className="mt-6 max-w-2xl text-mutedc leading-relaxed">
          A Chivasso, in Via Gerbido 15/3, un clinic dotato di TAC 3D e ortopantomografo
          dove ogni paziente è seguito dal primo incontro alla manutenzione — come faceva
          una volta il dentista di famiglia, con gli strumenti di oggi.
        </p>
      </section>

      {/* ---- STAFF ---- */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24" data-testid="studio-staff-section">
        <Reveal>
          <Eyebrow>Il nostro staff</Eyebrow>
          <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08] max-w-2xl">
            Le persone che ti accolgono e ti assistono, <span className="italic text-teal">ogni giorno.</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          <Reveal className="h-full">
            <figure className="rounded-[2rem] overflow-hidden border border-line bg-paper h-full" data-testid="staff-photo-francesca">
              <img src="/staff-francesca-full.jpg" alt="Francesca in sala operatoria" className="w-full aspect-[4/3] object-cover" />
              <CaptionReveal className="px-6 py-4 text-sm font-semibold text-ink">Francesca nella sala operatoria</CaptionReveal>
            </figure>
          </Reveal>
          <Reveal delay={0.08} className="h-full">
            <figure className="rounded-[2rem] overflow-hidden border border-line bg-paper h-full" data-testid="staff-photo-silvia">
              <img src="/staff-silvia.jpg" alt="Silvia nella sala operatoria con il logo Dentalchivasso" className="w-full aspect-[4/3] object-cover" />
              <CaptionReveal className="px-6 py-4 text-sm font-semibold text-ink">Silvia nella sala operatoria</CaptionReveal>
            </figure>
          </Reveal>
          <Reveal delay={0.12} className="h-full">
            <figure className="rounded-[2rem] overflow-hidden border border-line bg-paper h-full" data-testid="staff-photo-riggio">
              <img src="/lavoro-riggio.jpg" alt="Manuel Riggio e Silvia al lavoro" className="w-full aspect-[4/3] object-cover" />
              <CaptionReveal className="px-6 py-4 text-sm font-semibold text-ink">Manuel e Silvia al lavoro</CaptionReveal>
            </figure>
          </Reveal>
          <Reveal delay={0.16} className="h-full">
            <figure className="rounded-[2rem] overflow-hidden border border-line bg-paper h-full" data-testid="staff-photo-hola">
              <img src="/lavoro-hola.jpg" alt="Dr. Kola e Silvia al lavoro" className="w-full aspect-[4/3] object-cover" />
              <CaptionReveal className="px-6 py-4 text-sm font-semibold text-ink">Dr. Kola e Silvia al lavoro</CaptionReveal>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ---- DOTTOR GATTUSO ---- */}
      <section className="bg-sand/70 border-y border-line" data-testid="studio-gattuso-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full rounded-[2rem] border border-gold/40" />
              <img
                src={IMG.gattuso}
                alt="Dottor Massimo Filippo Gattuso"
                className="relative rounded-[2rem] w-full aspect-[4/5] max-h-[540px] object-cover object-top border border-line shadow-lift"
                data-testid="studio-gattuso-photo"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <Eyebrow>Il direttore degli interventi avanzati</Eyebrow>
              <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08]">
                Dott. Massimo Filippo Gattuso
              </h2>
              <p className="mt-3 text-teal font-semibold text-sm uppercase tracking-[0.14em]">
                Medico Chirurgo oro-maxillo-facciale
              </p>
              <p className="mt-6 text-mutedc leading-relaxed max-w-xl">
                Venticinque anni di esperienza in implantologia avanzata e docenza al Master
                dell'Università La Sapienza di Roma. Da 8 anni si occupa personalmente degli
                interventi di implantologia avanzata nel nostro studio: zigomatica, iuxtaossea,
                All-on-Four e All-on-Six.
              </p>
            </Reveal>
            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              {[
                { big: "25", small: "anni di esperienza" },
                { big: "8", small: "anni nel nostro studio" },
                { big: "La Sapienza", small: "docente Master, Roma" },
              ].map((s, i) => (
                <Reveal key={s.small} delay={i * 0.08}>
                  <div className="rounded-2xl bg-paper border border-line p-5" data-testid={`gattuso-stat-${i}`}>
                    <p className="font-display text-3xl font-semibold text-teal">{s.big}</p>
                    <p className="mt-1 text-xs text-mutedc leading-snug">{s.small}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---- TEAM ---- */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24" data-testid="studio-team-section">
        <Reveal>
          <Eyebrow>Il team</Eyebrow>
          <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08] max-w-2xl">
            Ogni specialità, <span className="italic text-teal">una persona dedicata.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="mt-12 rounded-[2rem] border border-line bg-paper overflow-hidden grid md:grid-cols-5" data-testid="studio-riggio-card">
            <img
              src="/team-riggio-v2.jpg"
              alt="Manuel Riggio, igienista dentale e direttore di Clinica"
              className="md:col-span-2 w-full h-full min-h-[300px] max-h-[420px] object-cover"
              data-testid="studio-riggio-photo"
            />
            <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Direzione clinica & relazioni con i pazienti</p>
              <p className="mt-3 font-display text-3xl md:text-4xl font-semibold text-ink">Manuel Riggio</p>
              <p className="mt-2 text-sm text-teal font-semibold">Igienista dentale e direttore di Clinica</p>
              <p className="mt-4 text-sm md:text-base text-mutedc leading-relaxed max-w-xl">
                Da 10 anni si occupa del rapporto con i pazienti: ti accoglie, ti segue e rende
                ogni visita semplice e chiara, dal primo contatto ai controlli periodici.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 rounded-[2rem] border border-line bg-paper overflow-hidden grid md:grid-cols-5" data-testid="studio-giorgi-card">
            <img
              src="/team-giorgi.jpg"
              alt="Dr. Matteo Giorgi, odontoiatra e chirurgo orale"
              className="md:col-span-2 w-full h-full min-h-[260px] max-h-[360px] object-cover object-top md:order-2"
            />
            <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center md:order-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Chirurgia orale & implantologia in carenza d'osso</p>
              <p className="mt-3 font-display text-3xl md:text-4xl font-semibold text-ink">Dr. Matteo Giorgi</p>
              <p className="mt-2 text-sm text-teal font-semibold">Odontoiatra e chirurgo orale</p>
              <p className="mt-4 text-sm md:text-base text-mutedc leading-relaxed max-w-xl">
                Da oltre 10 anni si occupa di implantologia zigomatica e iuxtaossea in carenza
                d'osso. Punto di riferimento in Italia e all'estero, relatore in convegni in
                tutto il mondo.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-8 rounded-[2rem] border border-line bg-paper overflow-hidden grid md:grid-cols-5" data-testid="studio-hola-card">
            <img
              src="/team-hola.jpg"
              alt="Dr. Dritan Kola, odontoiatra"
              className="md:col-span-2 w-full h-full min-h-[260px] max-h-[360px] object-cover object-top"
            />
            <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Protesi · Endodonzia · Conservativa</p>
              <p className="mt-3 font-display text-3xl md:text-4xl font-semibold text-ink">Dr. Dritan Kola</p>
              <p className="mt-2 text-sm text-teal font-semibold">Odontoiatra</p>
              <p className="mt-4 text-sm md:text-base text-mutedc leading-relaxed max-w-xl">
                Si occupa di protesi, endodonzia, conservativa e piccola chirurgia: i trattamenti
                di ogni giorno eseguiti con precisione, per mantenere il sorriso sano nel tempo.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-8 rounded-[2rem] border border-line bg-paper overflow-hidden grid md:grid-cols-5" data-testid="studio-mazzariol-card">
            <img
              src="/team-mazzariol-hd.jpg"
              alt="Dr.ssa Patricia Mazzariol, odontoiatra"
              className="md:col-span-2 w-full h-full min-h-[300px] max-h-[420px] object-cover md:order-2"
            />
            <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center md:order-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Odontoiatria generale & estetica</p>
              <p className="mt-3 font-display text-3xl md:text-4xl font-semibold text-ink">Dr.ssa Patricia Mazzariol</p>
              <p className="mt-2 text-sm text-teal font-semibold">Odontoiatra</p>
              <p className="mt-4 text-sm md:text-base text-mutedc leading-relaxed max-w-xl">
                Da oltre 20 anni si occupa di odontoiatria generale ed estetica: faccette
                estetiche e ricostruzioni per un sorriso naturale, curato nei minimi dettagli.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-8 rounded-[2rem] border border-line bg-paper overflow-hidden" data-testid="studio-staff-card">
            <div className="grid grid-cols-2">
              <div className="relative">
                <img
                  src="/team-francesca.jpg"
                  alt="Francesca, ASO e receptionist"
                  className="w-full h-full min-h-[240px] max-h-[400px] object-cover object-top"
                />
                <span className="absolute bottom-4 left-4 rounded-full bg-ink/70 text-ivory text-xs font-semibold px-4 py-1.5 backdrop-blur">
                  Francesca · Reception
                </span>
              </div>
              <div className="relative">
                <img
                  src="/team-silvia-v2.jpg"
                  alt="Silvia, ASO, nella sala operatoria"
                  className="w-full h-full min-h-[240px] max-h-[400px] object-cover"
                />
                <span className="absolute bottom-4 left-4 rounded-full bg-teal text-white text-xs font-semibold px-4 py-1.5">
                  Silvia · ASO
                </span>
              </div>
            </div>
            <div className="p-8 md:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Accoglienza & assistenza allo studio</p>
              <p className="mt-3 font-display text-3xl md:text-4xl font-semibold text-ink">Francesca e Silvia</p>
              <p className="mt-4 text-sm md:text-base text-mutedc leading-relaxed max-w-3xl">
                <strong className="text-ink">Francesca</strong>, ASO e receptionist, si occupa dell'accoglienza
                dei pazienti e del customer care: ogni visita inizia e finisce con lei.
                <strong className="text-ink"> Silvia</strong>, ASO, assiste il medico in tutte le fasi del
                trattamento. Entrambe si dedicano a mantenere lo studio in perfette condizioni
                e alla sterilizzazione degli strumenti.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-3 gap-5">
          {TEAM_ROLES.map((m, i) => (
            <Reveal key={m.t} delay={i * 0.08} className="h-full">
              <div className="rounded-3xl border border-line bg-paper p-8 h-full hover:border-teal/40 transition-colors duration-300" data-testid={`studio-team-card-${i}`}>
                <m.icon size={28} className="text-teal" strokeWidth={1.6} />
                <p className="mt-5 font-display text-xl font-semibold text-ink">{m.t}</p>
                <p className="mt-2 text-sm text-mutedc leading-relaxed">{m.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 md:px-8 pb-16 text-center">
        <LearnMore to="/contatti" testid="learn-more-studio-team" />
      </div>

      {/* ---- GARANZIE ---- */}
      <section className="bg-ink text-ivory relative overflow-hidden grain" data-testid="studio-garanzie-section">
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-teal/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24">
          <Reveal>
            <Eyebrow light>Garanzie</Eyebrow>
            <h2 className="mt-5 font-display font-medium tracking-tight text-4xl md:text-5xl max-w-2xl leading-[1.08]">
              Qualità che puoi <span className="italic text-teal-mist">verificare.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {CERTS.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08} className="h-full">
                <div className="rounded-3xl border border-ivory/15 bg-ivory/5 p-8 h-full" data-testid={`studio-garanzia-${i}`}>
                  <c.icon size={26} className="text-gold" strokeWidth={1.7} />
                  <p className="mt-5 font-semibold">{c.t}</p>
                  <p className="mt-2 text-sm text-ivory/60 leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 rounded-[2rem] overflow-hidden border border-ivory/15 bg-teal/10" data-testid="studio-closing-quote">
              <p className="px-8 py-12 md:px-16 md:py-16 font-display text-2xl md:text-4xl text-ivory text-center leading-relaxed">
                “Tecnologia all'avanguardia, <span className="italic text-teal-mist">calore di famiglia.</span>”
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---- RECENSIONI ---- */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24" data-testid="studio-testimonial-section">
        <Reveal>
          <div className="grid lg:grid-cols-12 gap-10 items-center rounded-[2.5rem] border border-line bg-teal-mist/40 p-7 md:p-12">
            <div className="lg:col-span-5">
              <Eyebrow>Recensioni dei pazienti</Eyebrow>
              <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08]">
                Chi ci sceglie, <span className="italic text-teal">torna e consiglia.</span>
              </h2>
              <div className="mt-8">
                <GoogleBadge />
              </div>
            </div>
            <div className="lg:col-span-7">
              <figure
                className="rounded-3xl border border-line bg-paper p-8 md:p-10"
                data-testid="studio-testimonial-card"
              >
                <div className="flex items-center justify-between">
                  <div className="flex gap-1 text-gold" aria-label="Valutazione 5 stelle su 5">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={16} fill="currentColor" strokeWidth={0} />
                    ))}
                  </div>
                  <Quote size={30} className="text-teal/25" strokeWidth={1.5} />
                </div>
                <blockquote className="mt-6 font-display text-lg md:text-xl leading-relaxed text-ink/85 whitespace-pre-line">
                  “{REAL_REVIEW.text}”
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-line flex items-center justify-between flex-wrap gap-2">
                  <p className="font-semibold text-sm text-ink">{REAL_REVIEW.name}</p>
                  <p className="text-xs text-mutedc">{REAL_REVIEW.tag}</p>
                </figcaption>
              </figure>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}